import { Component } from '@angular/core';

import { ConfigureService } from '@app/configure/services/configure.service';
import { ModalService } from '@app/core/services/modal.service';
import { HelperService } from '@app/core/services/helper.service';

import { FinanceQuote } from '@app/core/models/finance-quote.model';
import { ModalAction } from '@app/core/models/modal-action.model';
import { Modal } from '@app/core/models/modal.model';
import { AddOn } from '@app/sale-presentation/models/add-on.model';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
	selector: 'cng-addon-modal',
	templateUrl: './addon.component.html',
	styleUrls: ['./addon.component.less'],
})
export class ConfigureAddonModalComponent {
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;

	filter_products_form: FormGroup;
	filter_products_values: any = {
		max_price: 0,
		max_term: 0,
		max_claim_limit: 0
	}

	addon_index: number;
	addon: any;
	original_addon: any;

	finance_available: boolean = this.configure_service.configure_data.meta.finance.available;
	finance_failed: boolean = this.configure_service.configure_data.meta.finance.failed;
	finance_locked: boolean = this.configure_service.configure_data.meta.finance.locked;
	cashback_deal: boolean = this.configure_service.configure_data.meta.cashback_deal;
	show_addon_finance: boolean;

	stacked_quotes: any = this.configure_service.configure_data.steps.finance_options.data;
	selected_quote: FinanceQuote = this.stacked_quotes.find((quote) => {
		return quote.selected;
	});

	selected_options: any = {
		items: [],
		total_cash: 0,
		total_per_month: 0,
	};
	state_changed: boolean = false;

	max_claim_limit: number;
	max_term: number;
	max_price: number;

	price_step = 100;
	claim_step = 500;

	constructor(
		private configure_service: ConfigureService,
		private helper_service: HelperService,
		private modal_service: ModalService
	) {
		this.constructForm();
		this.constructModal();

		this.max_claim_limit = Math.max(...this.addon.options.map(o => o.claim_limit).filter(o => o !== undefined), 0);
        this.max_term = Math.max(...this.addon.options.map(o => o.term).filter(o => o !== undefined), 0);
		this.max_price = Math.max(...this.addon.options.map(o => o.amount).filter(o => o !== undefined), 0);

		this.max_claim_limit = Math.ceil(this.max_claim_limit / this.claim_step) * this.claim_step;
		this.max_price = Math.ceil(this.max_price / this.price_step) * this.price_step;
	}

	constructForm(): void {
		this.filter_products_form = new FormGroup({
			max_price: new FormControl(0),
			max_term: new FormControl(0),
			max_claim_limit: new FormControl(0),
		});
	}

	constructModal(): void {
		this.addon_index = this.modal_service.data.addon_index;
		this.addon = this.helper_service.copy(
			this.configure_service.configure_data.steps.customise_review.data.addons[this.addon_index]
		);
		this.original_addon = this.helper_service.copy(
			this.configure_service.configure_data.steps.customise_review.data.addons[this.addon_index]
		);
		this.show_addon_finance =
			this.finance_available && !this.finance_failed && !this.cashback_deal && this.addon.financeable;

		this.setSelectedOptions();

		this.modal_service.setConfig(
			new Modal({
				name: 'addon_modal',
				title: this.addon.description + ' Info',
				size: 'sm',
				height: 'full',
				actions: [
					new ModalAction({
						id: 'save_options',
						name: 'Save Options',
						bias: 'positive',
						showLoading: true,
						large: true,
						click: (action: ModalAction) => this.onSave(action),
					}),
				],
			})
		);

		this.configure_service.track('modal.open', { modal: 'addon-options' }).subscribe();
	}

	onSave(action: ModalAction): void {
		if (this.finance_locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		for (let i = 0; i < this.addon.options.length; i++) {
			if (this.addon.options[i].selected && !this.original_addon.options[i].selected) {
				// selected
				this.configure_service.track('accessory.add', { accessory: this.addon.options[i] }).subscribe();
			}

			if (!this.addon.options[i].selected && this.original_addon.options[i].selected) {
				// deselected
				this.configure_service.track('accessory.delete', { accessory: this.addon.options[i] }).subscribe();
			}
		}

		this.configure_service.configure_data.steps.customise_review.data.addons[this.addon_index] = this.addon;
		this.configure_service.saveCustomiseReview().subscribe(() => {
			action.loading = false;
			this.modal_service.close();
		});
	}

	setSelectedOptions(): void {
		this.selected_options.items = this.addon.options.filter((o) => {
			return o.selected;
		});

		this.selected_options.total_cash = this.selected_options.items.reduce((total, option) => {
			return total + option.amount;
		}, 0);

		this.selected_options.total_per_month = this.selected_options.items.reduce((total, option) => {
			return total + option.per_month;
		}, 0);
	}

	toggleOption(option_index): void {
		const options = this.getOptions();

		if (!options[option_index].required) {
			if (!this.addon.allow_multiple) {
				options.forEach((option, index) => {
					if (index !== option_index && !option.required) {
						option.selected = false;
					}
				});
			}
			options[option_index].selected = !options[option_index].selected;
			this.setSelectedOptions();
			this.state_changed = true;
		}
	}

	buildAddonLogoUrl(filename: string): string {
		return this.helper_service.buildAddonBrandLogoUrl(filename);
	}

	hasOrganisationProducts(): boolean {
		return this.addon.options.filter(o => o.organisation_product_id).length > 0;
	}

	getTitleText(o: AddOn) {
		if (!o.organisation_product_id || !o.has_product) {
			return o.description;
		}

		return o.provider_name + ' ' + o.product_type_name + ' Warranty';
	}

	getMaxPriceLabel(): string {
		const max_price = this.filter_products_form.value.max_price;

		return max_price ? max_price : 'All';
	}

	getMaxTermLabel(): string {
		const max_term = this.filter_products_form.value.max_term;

		return max_term ? max_term : 'All';
	}

	getClaimLimitLabel(): string {
		const claim_limit = this.filter_products_form.value.max_claim_limit;

		return claim_limit ? claim_limit : 'All';
	}

	setFilters(): void {
		this.filter_products_values = this.filter_products_form.value;
	}

	getOptions(): any {
		const options = this.addon.options;

		return options.filter((addOnOption) => {
			return (
				(
					(this.filter_products_values.max_claim_limit === 0 || addOnOption.claim_limit <= this.filter_products_values.max_claim_limit) &&
					(this.filter_products_values.max_term === 0 || addOnOption.term <= this.filter_products_values.max_term) &&
					(this.filter_products_values.max_price === 0 || addOnOption.amount <= this.filter_products_values.max_price)
				) ||
				addOnOption.selected
			);
		});
	}
}
